import React, { useEffect, useState } from "react";
import { Box, Flex, HStack, Image, Link, Spinner, Text } from "@chakra-ui/react";
import { FiFileText, FiLock } from "react-icons/fi";
import { tt } from "../../i18n/tt";
import { fmtDateTime } from "../../lib/formatLocale";
import { formatBytes, isPreviewableImage, signedUrlOf } from "../../lib/opsChat";

/**
 * 운영팀 대화방 — 메시지 목록 (크리에이터 시트·PC 창 · 어드민 대화방 관리 공용)
 *   viewer = "creator" : 내 메시지(크리에이터) 오른쪽 · 운영팀 왼쪽
 *   viewer = "admin"   : 운영팀 메시지 오른쪽 · 크리에이터 왼쪽 · 담당자 변경 줄도 보인다(서버가 크리에이터에게는 안 준다)
 * 본문은 평문(whiteSpace pre-wrap) — HTML 로 그리지 않는다.
 * docs/ops/ops-chat-room-plan.md §3
 */
export default function OpsChatMessages({ messages, viewer = "creator", compact = false, otherReadAt = null, laySummary = null }) {
  const readAt = otherReadAt ? new Date(otherReadAt).getTime() : null;
  return (
    <Flex direction="column" gap={3}>
      {laySummary && laySummary.length > 0 && <LaySummaryCard items={laySummary} />}
      {(messages || []).map((m) => (m.role === "system"
        ? <SystemLine key={m.id} m={m} viewer={viewer} />
        : <Bubble key={m.id} m={m} viewer={viewer} compact={compact} unread={isUnreadByOther(m, viewer, readAt)} />))}
    </Flex>
  );
}

/** 내가 보낸 메시지를 상대가 아직 안 읽었나(읽음 「1」) — 상대가 읽은 시각보다 뒤에 보낸 내 메시지 */
export function isUnreadByOther(m, viewer, readAtMs) {
  const mine = viewer === "admin" ? m.role === "admin" : m.role === "creator";
  if (!mine) return false;
  if (readAtMs === null || readAtMs === undefined) return true;
  return new Date(m.created_at).getTime() > readAtMs;
}

/** 크리에이터가 상담사 연결 전에 레이와 나눈 대화(질문·답) — 접어서 보여 준다 */
function LaySummaryCard({ items }) {
  const [open, setOpen] = useState(false);
  const shown = open ? items : items.slice(0, 1);
  return (
    <Box border="1px dashed" borderColor="brand.200" bg="white" borderRadius="10px" px={3} py={2.5}>
      <Text fontSize="12.5px" fontWeight="700" color="brand.700" mb={1}>{tt("a.60e3bad4", "레이와 나눈 대화")}</Text>
      {shown.map((it, i) => (
        <Box key={i} mb={i < shown.length - 1 ? 2 : 0}>
          <Text fontSize="13px" whiteSpace="pre-wrap"><b>Q.</b> {it.q}</Text>
          {it.a && <Text fontSize="12.5px" color="neutral.600" whiteSpace="pre-wrap" noOfLines={open ? undefined : 2}>
            <b>{tt("a.dc77f3e7", "레이.")}</b> {it.unknown ? tt("a.4c17c748", "(레이가 답하지 못했어요)") : it.a}
          </Text>}
        </Box>
      ))}
      {items.length > 1 || (items[0]?.a || "").length > 80 ? (
        <Box as="button" type="button" mt={1} fontSize="12px" color="brand.700" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
          {open ? tt("a.365ca4c0", "접기") : tt("a.26386354", "대화 전체 보기")}
        </Box>
      ) : null}
    </Box>
  );
}

function SystemLine({ m, viewer }) {
  if (m.system_kind === "offhours") {
    return (
      <Box alignSelf="stretch" px={3} py={2} borderRadius="10px" bg="statusWash.warning" color="status.warning" fontSize="13px" role="note">
        🌙 {m.body}
      </Box>
    );
  }
  let text;
  if (m.system_kind === "opened") text = viewer === "admin" ? `${tt("a.d3ee038f", "대화방을 열었어요")}` : tt("a.7997ea31", "운영팀이 대화방을 열었어요");
  else if (m.system_kind === "connected") text = viewer === "admin" ? tt("a.35a47d07", "크리에이터가 상담사 연결을 요청했어요") : tt("a.1f3adb97", "상담사 연결을 요청했어요");
  else if (m.system_kind === "accepted") text = tt("a.e1a6ba95", "{{v0}} 님이 상담을 시작했어요", { v0: m.body });
  else if (m.system_kind === "closed") text = viewer === "admin" ? tt("a.68c82c00", "운영팀이 대화를 마쳤어요") : tt("a.e67860e3", "상담이 끝났어요");
  else text = tt("a.e953a600", "담당자를 {{v0}} 님으로 바꿨어요", { v0: m.body });
  return (
    <HStack alignSelf="center" spacing={1.5} px={3} py={1} borderRadius="full" bg="neutral.100" color="neutral.600" fontSize="12px">
      {m.system_kind === "closed" && <FiLock aria-hidden="true" />}
      <Text as="span">{text} · {fmtDateTime(m.created_at)}</Text>
    </HStack>
  );
}

function Bubble({ m, viewer, compact, unread = false }) {
  const right = viewer === "admin" ? m.role === "admin" : m.role === "creator";
  const showName = !right || viewer === "admin";
  return (
    <Flex direction="column" align={right ? "flex-end" : "flex-start"} gap={1} maxW={compact ? "88%" : "75%"} alignSelf={right ? "flex-end" : "flex-start"}>
      {showName && m.sender_name && <Text fontSize="12px" color="neutral.600">{m.sender_name}</Text>}
      {(m.attachments || []).length > 0 && (
        <Flex gap={2} wrap="wrap" justify={right ? "flex-end" : "flex-start"}>
          {m.attachments.map((a) => <Attachment key={a.id} a={a} />)}
        </Flex>
      )}
      {m.body && (
        <Box
          px={3} py={2}
          bg={right ? "brand.50" : "white"}
          border="1px solid"
          borderColor={right ? "brand.100" : "neutral.200"}
          borderRadius={right ? "14px 4px 14px 14px" : "4px 14px 14px 14px"}
        >
          <Text fontSize="14px" lineHeight="1.55" color="neutral.800" whiteSpace="pre-wrap" wordBreak="break-word">{m.body}</Text>
        </Box>
      )}
      <HStack spacing={1.5} fontSize="11px" color="neutral.500">
        {unread && <Text as="span" color="accent.600" fontWeight="800" aria-label={tt("a.e3ce87f3", "상대가 아직 읽지 않음")}>1</Text>}
        <Text as="span">{fmtDateTime(m.created_at)}</Text>
      </HStack>
    </Flex>
  );
}

function Attachment({ a }) {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  const image = isPreviewableImage(a.mime_type) && a.path;

  useEffect(() => {
    let alive = true;
    if (!image) return undefined;
    signedUrlOf(a.path).then((u) => { if (alive) setUrl(u); }).catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, [a.path, image]);

  if (a.deleted || !a.path) {
    return (
      <Box px={3} py={2} border="1px dashed" borderColor="neutral.300" borderRadius="12px" fontSize="12px" color="neutral.500">
        {tt("a.6b96114f", "보관 기간이 지나 삭제된 파일")} · {a.file_name}
      </Box>
    );
  }

  const openOriginal = async (e) => {
    e.preventDefault();
    // 서명 주소를 받는 동안 기다리면 팝업 차단에 걸린다 — 창을 먼저 열고 주소를 나중에 넣는다
    const w = window.open("about:blank", "_blank");
    if (w) w.opener = null;
    try {
      const u = await signedUrlOf(a.path, { download: image ? undefined : a.file_name });
      if (u && w) w.location.href = u;
      else if (w) w.close();
    } catch {
      if (w) w.close();
      setFailed(true);
    }
  };

  if (image && !failed) {
    return (
      <Link href="#" onClick={openOriginal} aria-label={tt("a.732b13c5", "사진 크게 보기: {{v0}}", { v0: a.file_name })}
        display="block" w="180px" h="130px" borderRadius="12px" overflow="hidden" border="1px solid" borderColor="neutral.200" bg="neutral.100">
        {url ? <Image src={url} alt={a.file_name} w="100%" h="100%" objectFit="cover" /> : <Flex h="100%" align="center" justify="center"><Spinner size="sm" color="neutral.400" /></Flex>}
      </Link>
    );
  }

  return (
    <Link href="#" onClick={openOriginal} display="flex" alignItems="center" gap={2.5} px={3} py={2.5} minW="200px" maxW="260px"
      borderRadius="12px" border="1px solid" borderColor="neutral.200" bg="white" _hover={{ bg: "neutral.50", textDecoration: "none" }}>
      <Box as={FiFileText} boxSize="22px" color="neutral.500" flexShrink={0} aria-hidden="true" />
      <Box minW={0}>
        <Text fontSize="13.5px" fontWeight="600" color="neutral.800" noOfLines={1}>{a.file_name}</Text>
        <Text fontSize="12px" color="neutral.500">{formatBytes(a.size_bytes)} · {failed ? tt("a.1bf88f40", "열지 못했어요") : tt("a.49905a95", "내려받기")}</Text>
      </Box>
    </Link>
  );
}
